import React, { useState, useEffect, useCallback } from 'react'
import useEmblaCarousel, { EmblaOptionsType } from 'embla-carousel-react'
import Autoplay from 'embla-carousel-autoplay'

type SlideData = {
  title: string;
  description: string;
  imageUrl: string;
};

const slidesData: SlideData[] = [
  { 
    title: "Fique por dentro: |as alterações da NR 1",
    description: "A SALUS oferece soluções completas em Saúde e Segurança do Trabalho, garantido a conformidade com a nova NR 1 e todas as demais. Fale com quem entende! ",
    imageUrl: "/images/carousel/carousel-1.svg"
  },
  { 
    title: "Avaliação Descomplicada |das Suas Documentações",
    description: "Solicite uma avaliação gratuita* dos seus documentos de SST: PGR, PCMSO e LTCAT e veja se eles estão em conformidade legal.",
    imageUrl: "/images/carousel/carousel-2.svg"
  },
  { 
    title: "Treinamentos Obrigatórios |para Toda a Sua Equipe",
    description: "De forma descomplicada e para qualquer lugar do Brasil. Capacite seus colaboradores com nossos cursos EAD e presenciais sobre NRs, CIPA, primeiros socorros e muito mais.",
    imageUrl: "/images/carousel/carousel-3.svg"
  },
];

const HeroCarousel: React.FC = () => {
  const options: EmblaOptionsType = { loop: true };
  const [emblaRef, emblaApi] = useEmblaCarousel(options, [Autoplay({ delay: 7000, stopOnInteraction: false })]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);

  const scrollTo = useCallback((index: number) => emblaApi && emblaApi.scrollTo(index), [emblaApi]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi, setSelectedIndex]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    setScrollSnaps(emblaApi.scrollSnapList());
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
    // Clean up listener on unmount
    return () => {
      emblaApi.off('select', onSelect);
    }
  }, [emblaApi, setScrollSnaps, onSelect]);

  return (
    <div className="relative w-full text-salus-branco">
      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex"> {/* embla__container */}
          {slidesData.map((slide, index) => {
            const [titlePart1, titlePart2] = slide.title.split('|');
            return (
              <div className="flex-[0_0_100%] min-w-0 px-2 sm:px-4 lg:px-6" key={index}> {/* embla__slide */}
                {/* Layout responsivo: mobile sem imagem, desktop com grid */}
                <div className="lg:grid lg:grid-cols-5 lg:gap-8 lg:items-center h-full lg:min-h-[500px]">
                  {/* Content Section - ocupa tela toda no mobile, 3/5 no desktop */}
                  <div className="w-full lg:col-span-3 space-y-4 lg:space-y-6">
                    <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold leading-tight font-heading">
                      <span className="block">{titlePart1}</span>
                      {titlePart2 && <span className="text-salus-verde-claro block">{titlePart2}</span>}
                    </h1>
                    <p className="text-base sm:text-lg lg:text-xl leading-relaxed text-salus-branco-secundario max-w-2xl">
                      {slide.description}
                    </p>
                  </div>
                  
                  {/* Image Section - apenas no desktop */}
                  <div className="hidden lg:flex w-full lg:col-span-2 justify-end">
                    <div className="relative w-full max-w-[380px] xl:max-w-[420px]">
                      <img 
                        src={slide.imageUrl} 
                        alt={`Ilustração para ${titlePart1}`}
                        className="w-full h-auto object-contain transition-all duration-700 ease-out transform hover:scale-105"
                        style={{
                          filter: 'drop-shadow(0 10px 25px rgba(45, 143, 95, 0.2))',
                          animation: selectedIndex === index ? 'fadeInSlide 0.8s ease-out' : 'none'
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Imagem removida do mobile para manter layout original */}

      {/* Nota sobre condições da avaliação gratuita - apenas para slide de Avaliação Descomplicada */}
      {selectedIndex === 1 && (
        <div className="absolute bottom-4 right-4 text-xs text-salus-branco/70">
          *verifique condições da avaliação gratuita
        </div>
      )}

      {/* Progress Bar */}
      <div className="absolute bottom-[-20px] left-1/2 transform -translate-x-1/2 w-64 h-1 bg-salus-branco/30 rounded-full overflow-hidden">
        <div 
          className="h-full bg-gradient-to-r from-salus-verde-claro to-salus-verde-escuro rounded-full transition-all duration-500 ease-out"
          style={{ 
            width: `${((selectedIndex + 1) / scrollSnaps.length) * 100}%` 
          }}
        />
        {/* Clickable segments for navigation */}
        <div className="absolute inset-0 flex">
          {scrollSnaps.map((_, index) => (
            <button
              key={index}
              onClick={() => scrollTo(index)}
              className="flex-1 h-full cursor-pointer hover:bg-salus-branco/10 transition-colors"
              aria-label={`Ir para slide ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

// Adicionar estilos CSS para animações
const styles = `
  @keyframes fadeInSlide {
    0% {
      opacity: 0;
      transform: translateX(30px) scale(0.95);
    }
    100% {
      opacity: 1;
      transform: translateX(0) scale(1);
    }
  }
  
  .carousel-image {
    transition: all 0.7s cubic-bezier(0.4, 0, 0.2, 1);
  }
  
  .carousel-image:hover {
    transform: scale(1.05) rotate(1deg);
    filter: drop-shadow(0 15px 35px rgba(45, 143, 95, 0.3)) brightness(1.05);
  }
`;

// Injetar estilos no documento
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = styles;
  document.head.appendChild(styleSheet);
}

export default HeroCarousel;